import { MonitorIcon, MoonIcon, SunIcon } from "lucide-react"
import { ThemeProvider, useTheme } from "next-themes"
import type { ReactNode } from "react"
import { Segmented } from "./segmented.tsx"

/*
 * Which colour scheme the web app renders in. `system` follows the machine's own setting and is
 * where everyone starts; the other two are a choice remembered in this browser.
 *
 * `next-themes` does the work — it is already here because the vendored toast reads the scheme
 * from it — and puts the `dark` class on `<html>` that `tokens.css` keys its dark scheme on.
 */
export const colourSchemes = [
  { value: "system", label: "System", Icon: MonitorIcon },
  { value: "light", label: "Light", Icon: SunIcon },
  { value: "dark", label: "Dark", Icon: MoonIcon },
] as const

export type ColourScheme = (typeof colourSchemes)[number]["value"]

const isColourScheme = (value: unknown): value is ColourScheme =>
  colourSchemes.some((scheme) => scheme.value === value)

export function ColourSchemeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      storageKey="beacon-colour-scheme"
    >
      {children}
    </ThemeProvider>
  )
}

/**
 * The person's chosen scheme and a way to change it, for a control that isn't the one below (the
 * account menu's Theme submenu, the command palette's Theme actions). `chooseScheme` takes what a
 * menu hands back untyped, and changes the scheme only when it names one.
 */
export function useColourScheme(): {
  scheme: ColourScheme
  setScheme: (scheme: ColourScheme) => void
  chooseScheme: (value: unknown) => void
} {
  const { theme, setTheme } = useTheme()
  return {
    scheme: isColourScheme(theme) ? theme : "system",
    setScheme: setTheme,
    chooseScheme: (value) => isColourScheme(value) && setTheme(value),
  }
}

const schemeOptions = colourSchemes.map(({ value, label, Icon }) => ({ value, label, icon: Icon }))

/** The scheme as a Segmented control, all three choices in view: System, Light, Dark. */
export function ColourSchemeToggle() {
  const { scheme, setScheme } = useColourScheme()
  return (
    <Segmented label="Colour scheme" value={scheme} options={schemeOptions} onChange={setScheme} />
  )
}
